import { CalendarDays, Users, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DatePicker } from '@/components/shared/date-picker'
import type { OrganizationUser } from '@/gen/cortado/v1/organizations_pb'
import { formatFullName } from '@/lib/utils'

interface PipelineToolbarProps {
  search: string
  onSearchChange: (value: string) => void
  eventDateFrom?: Date
  eventDateTo?: Date
  onEventDateChange: (range: { from?: Date; to?: Date }) => void
  ownerId: string
  onOwnerChange: (ownerId: string) => void
  owners: OrganizationUser[]
  showingLabel: string
}

export function PipelineToolbar({
  search,
  onSearchChange,
  eventDateFrom,
  eventDateTo,
  onEventDateChange,
  ownerId,
  onOwnerChange,
  owners,
  showingLabel,
}: PipelineToolbarProps) {
  const hasEventDateFilter = !!(eventDateFrom || eventDateTo)

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder="Search customer, company, quote #"
        aria-label="Search pipeline"
        className="w-70 bg-card"
      />

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            <CalendarDays />
            {hasEventDateFilter ? 'Event date: custom' : 'Event date'}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-70">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <div className="text-sm font-medium">From</div>
              <DatePicker
                value={eventDateFrom}
                onChange={(date) => onEventDateChange({ from: date, to: eventDateTo })}
                placeholder="Select start date"
              />
            </div>
            <div className="space-y-1.5">
              <div className="text-sm font-medium">To</div>
              <DatePicker
                value={eventDateTo}
                onChange={(date) => onEventDateChange({ from: eventDateFrom, to: date })}
                placeholder="Select end date"
                minDate={eventDateFrom}
              />
            </div>
            {hasEventDateFilter && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-center"
                onClick={() => onEventDateChange({ from: undefined, to: undefined })}
              >
                <X />
                Clear event date
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <Select value={ownerId} onValueChange={onOwnerChange}>
        <SelectTrigger size="sm" className="bg-card" aria-label="Filter by owner">
          <Users className="text-muted-foreground" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">Owner</SelectItem>
          {owners
            .filter((owner) => !!owner.user?.id)
            .map((owner) => (
              <SelectItem key={owner.user!.id} value={owner.user!.id}>
                {formatFullName(owner.firstName, owner.lastName, owner.email)}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>

      <span className="ml-auto text-xs text-muted-foreground">{showingLabel}</span>
    </div>
  )
}
